// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * What the entry popup shows of a log row: the row's message JSON (the board
 * message as windmill serializes it) read into one typed view, whatever the
 * statement kind. Nothing here is translated; the popup labels it.
 */
export var ELogOrigin;
(function (ELogOrigin) {
    ELogOrigin["HASURA"] = "hasura";
    ELogOrigin["HARVEST"] = "harvest";
    ELogOrigin["WINDMILL"] = "windmill";
    ELogOrigin["KEYCLOAK_CONSOLE"] = "keycloak-console";
    ELogOrigin["SERVICE_ACCOUNT"] = "service-account";
})(ELogOrigin || (ELogOrigin = {}));
export var ELogChangeKind;
(function (ELogChangeKind) {
    ELogChangeKind["VALUES"] = "values";
    /** A secret field: the entry only says that it changed. */
    ELogChangeKind["SECRET"] = "secret";
    /** A text too long to keep: its SHA-256 before and after. */
    ELogChangeKind["HASHES"] = "hashes";
})(ELogChangeKind || (ELogChangeKind = {}));
/** How the checksum an admin declared for an imported file compares with the file's. */
export var EImportChecksum;
(function (EImportChecksum) {
    EImportChecksum["UNDECLARED"] = "undeclared";
    EImportChecksum["MATCHES"] = "matches";
    EImportChecksum["MISMATCH"] = "mismatch";
})(EImportChecksum || (EImportChecksum = {}));
export var STATEMENT_KIND_VOTERS_IMPORTED = "VotersImported";
export var STATEMENT_KIND_READINESS_VERIFIED = "ReadinessVerified";
export var STATEMENT_KIND_KEYCLOAK_USER_EVENT = "KeycloakUserEvent";
export var STATEMENT_KIND_ADMIN_CHANGE = "AdminChange";
export var STATEMENT_KIND_KEYCLOAK_ADMIN_EVENT = "KeycloakAdminEvent";
export var STATEMENT_KIND_LOGS_EXPORTED = "LogsExported";
export var STATEMENT_KIND_LOG_WRITER_STARTED = "LogWriterStarted";
export var STATEMENT_KIND_LOG_WRITER_STOPPED = "LogWriterStopped";
export var STATEMENT_KIND_ADMIN_PUBLIC_KEY = "AdminPublicKey";
export var STATEMENT_KIND_TENANT_ADMIN_ACTION = "TenantAdminAction";
var MADE_BY_SERVICE_ACCOUNT = "SERVICE_ACCOUNT";
var FAILURE_DETAILS = ["num_failures", "numFailures"];
var isObject = function (value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
};
var parseObject = function (text) {
    if (typeof text !== "string")
        return null;
    try {
        var value = JSON.parse(text);
        return isObject(value) ? value : null;
    }
    catch (_a) {
        return null;
    }
};
/** A scalar as text; null for nothing, "null" (as the board writes a missing user) and objects. */
var text = function (value) {
    if (typeof value === "string")
        return value && value !== "null" ? value : null;
    if (typeof value === "number" || typeof value === "boolean")
        return String(value);
    return null;
};
var compact = function (value) {
    return typeof value === "string" ? value : JSON.stringify(value);
};
var listText = function (values) {
    return values
        .map(function (value) { return (isObject(value) || Array.isArray(value) ? compact(value) : String(value)); })
        .join(", ");
};
/**
 * A logged value as the popup shows it: text as written, a yes/no as a
 * boolean, a list joined by commas and an object as `key: value; key: value`.
 * Null stands for "no value" (absent, null or empty).
 */
export var logValue = function (value) {
    if (value === undefined || value === null || value === "")
        return null;
    if (typeof value === "boolean")
        return value;
    if (typeof value === "string")
        return value;
    if (typeof value === "number")
        return String(value);
    if (Array.isArray(value))
        return value.length ? listText(value) : null;
    if (isObject(value)) {
        var parts = Object.entries(value).map(function (_a) {
            var key = _a[0], inner = _a[1];
            var shown = logValue(inner);
            return "".concat(key, ": ").concat(shown === null ? "-" : String(shown));
        });
        return parts.length ? parts.join("; ") : null;
    }
    return String(value);
};
var changeOf = function (value) {
    if (!isObject(value) || typeof value.path !== "string")
        return null;
    var path = value.path;
    if (value.secret === true)
        return { kind: ELogChangeKind.SECRET, path: path };
    if ("old_sha256" in value || "new_sha256" in value) {
        return {
            kind: ELogChangeKind.HASHES,
            path: path,
            oldSha256: text(value.old_sha256),
            newSha256: text(value.new_sha256),
        };
    }
    return { kind: ELogChangeKind.VALUES, path: path, old: logValue(value.old), new: logValue(value.new) };
};
var CHANGE_ORIGINS = [ELogOrigin.HASURA, ELogOrigin.HARVEST, ELogOrigin.WINDMILL];
var changeOrigin = function (value) { var _a; return (_a = CHANGE_ORIGINS.find(function (origin) { return origin === value; })) !== null && _a !== void 0 ? _a : null; };
var rows = function (value, skip) {
    if (skip === void 0) { skip = []; }
    return Object.entries(value)
        .filter(function (_a) {
        var name = _a[0];
        return !skip.includes(name);
    })
        .map(function (_a) {
        var name = _a[0], inner = _a[1];
        return ({ name: name, value: logValue(inner) });
    })
        .filter(function (row) { return row.value !== null; })
        .map(function (_a) {
        var name = _a.name, shown = _a.value;
        return ({ name: name, value: String(shown) });
    });
};
var DELETED_EVENT_KEYS = [
    "log_database",
    "last_entry_id",
    "state_hash",
    "entries",
    "status",
    "task_id",
    "system_public_key",
];
var IMPORTED_FILE_KEYS = [
    "document_id",
    "file_sha256",
    "declared_sha256",
    "declared_checksum",
    "counts",
    "count",
];
var importChecksum = function (value) {
    return Object.values(EImportChecksum).includes(value)
        ? value
        : null;
};
var importedFileOf = function (extra) {
    var fileSha256 = text(extra === null || extra === void 0 ? void 0 : extra.file_sha256);
    if (!extra || !fileSha256)
        return null;
    var counts = isObject(extra.counts) ? rows(extra.counts) : [];
    var count = text(extra.count);
    return {
        documentId: text(extra.document_id),
        fileSha256: fileSha256,
        declaredSha256: text(extra.declared_sha256),
        declaredChecksum: importChecksum(extra.declared_checksum),
        counts: count === null ? counts : __spreadArray([{ name: "count", value: count }], counts, true),
    };
};
var deletedEventOf = function (extra) {
    var logDatabase = text(extra === null || extra === void 0 ? void 0 : extra.log_database);
    if (!extra || !logDatabase)
        return null;
    return {
        logDatabase: logDatabase,
        lastEntryId: text(extra.last_entry_id),
        stateHash: text(extra.state_hash),
        entries: text(extra.entries),
        status: text(extra.status),
        taskId: text(extra.task_id),
        systemPublicKey: text(extra.system_public_key),
    };
};
var signInOf = function (body) {
    if (!isObject(body))
        return null;
    var variant = body[STATEMENT_KIND_KEYCLOAK_USER_EVENT];
    if (!Array.isArray(variant))
        return null;
    var content = variant[0], event = variant[1];
    var details = parseObject(content);
    if (!details) {
        return {
            event: text(event),
            error: null,
            clientId: null,
            ipAddress: null,
            authentication: null,
            credential: null,
            failures: null,
            details: [],
            text: text(content),
        };
    }
    var rest = isObject(details.details) ? details.details : {};
    var failureKey = FAILURE_DETAILS.find(function (key) { return text(rest[key]) !== null; });
    return {
        event: text(event),
        error: text(details.error),
        clientId: text(details.client_id),
        ipAddress: text(details.ip_address),
        authentication: [text(details.auth_method), text(details.auth_type)]
            .filter(function (part) { return part !== null; })
            .join(" · ") || null,
        credential: text(details.credential_type),
        failures: failureKey ? text(rest[failureKey]) : null,
        details: rows(rest, FAILURE_DETAILS),
        text: null,
    };
};
var unknownAccount = function (body) {
    var _a;
    if (!isObject(body))
        return false;
    var variant = body[STATEMENT_KIND_KEYCLOAK_USER_EVENT];
    return Array.isArray(variant) && ((_a = parseObject(variant[0])) === null || _a === void 0 ? void 0 : _a.unknown_account) === true;
};
/** What a Keycloak admin event applies to: the named thing, else the resource it addresses. */
var appliesTo = function (representation, path) {
    var _a;
    if (isObject(representation)) {
        var named = (_a = text(representation.username)) !== null && _a !== void 0 ? _a : text(representation.name);
        if (named)
            return named;
    }
    return path ? path.split("/").slice(0, 2).join("/") : null;
};
var keycloakOf = function (details) {
    var actor = isObject(details.actor) ? details.actor : {};
    var resourcePath = text(details.resource_path);
    var representation = details.representation;
    return {
        origin: details.made_by === MADE_BY_SERVICE_ACCOUNT
            ? ELogOrigin.SERVICE_ACCOUNT
            : ELogOrigin.KEYCLOAK_CONSOLE,
        username: text(actor.username),
        userId: text(actor.user_id),
        realm: text(actor.realm),
        clientId: text(actor.client_id),
        ipAddress: text(actor.ip_address),
        operation: text(details.operation),
        resourceType: text(details.resource_type),
        resourcePath: resourcePath,
        appliesTo: appliesTo(representation, resourcePath),
        representation: representation === null || representation === undefined
            ? null
            : typeof representation === "string"
                ? representation
                : JSON.stringify(representation, null, 2),
        error: text(details.error),
    };
};
var exportOf = function (details) { return ({
    format: text(details.format),
    scope: text(details.scope),
    username: text(details.username),
    electionId: text(details.election_id),
    areaId: text(details.area_id),
    createdFrom: text(details.created_from),
    createdTo: text(details.created_to),
    timeZone: text(details.time_zone),
    entries: text(details.entries),
    sha256: text(details.sha256),
    documentId: text(details.document_id),
}); };
/** The details an Audit body carries, whatever its kind. */
var auditDetails = function (body) {
    if (!isObject(body) || !isObject(body.Audit))
        return null;
    return parseObject(body.Audit.details_json);
};
/**
 * The details of a TenantActivity body: a request Harvest recorded outside an
 * election event, or the opening of a tenant log.
 */
var tenantActivityDetails = function (body) {
    if (!isObject(body) || !isObject(body.TenantActivity))
        return null;
    return parseObject(body.TenantActivity.details_json);
};
var keyText = function (value) {
    return value === null || value === undefined || value === "" ? null : compact(value);
};
var emptyView = function (description, statementKind) { return ({
    description: description,
    unreadable: true,
    statementKind: statementKind,
    eventType: null,
    logType: null,
    who: { username: null, userId: null, role: null, unknownAccount: false },
    origin: null,
    object: null,
    action: null,
    changes: [],
    signIn: null,
    keycloak: null,
    deletedEvent: null,
    importedFile: null,
    exported: null,
    writer: null,
    extra: [],
    publicKey: null,
    signatures: { signedBy: null, senderKey: null, senderSignature: null, systemSignature: null },
}); };
/** The popup's view of a log row. A message that isn't JSON keeps only its text. */
export var logEntryView = function (record) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    var statementKind = text(record === null || record === void 0 ? void 0 : record.statement_kind);
    var message = parseObject(record === null || record === void 0 ? void 0 : record.message);
    if (!message)
        return emptyView((_a = record === null || record === void 0 ? void 0 : record.message) !== null && _a !== void 0 ? _a : "", statementKind);
    var statement = isObject(message.statement) ? message.statement : {};
    var head = isObject(statement.head) ? statement.head : {};
    var body = statement.body;
    var kind = statementKind !== null && statementKind !== void 0 ? statementKind : text(head.kind);
    var details = auditDetails(body);
    var sender = isObject(message.sender) ? message.sender : {};
    var view = __assign(__assign({}, emptyView((_b = text(head.description)) !== null && _b !== void 0 ? _b : "", kind)), { unreadable: false, eventType: text(head.event_type), logType: text(head.log_type), who: {
            username: (_c = text(message.username)) !== null && _c !== void 0 ? _c : text(record === null || record === void 0 ? void 0 : record.username),
            userId: (_d = text(message.user_id)) !== null && _d !== void 0 ? _d : text(record === null || record === void 0 ? void 0 : record.user_id),
            role: null,
            unknownAccount: unknownAccount(body),
        }, signIn: kind === STATEMENT_KIND_KEYCLOAK_USER_EVENT ? signInOf(body) : null, signatures: {
            signedBy: text(sender.name),
            senderKey: keyText(sender.pk),
            senderSignature: keyText(message.sender_signature),
            systemSignature: keyText(message.system_signature),
        } });
    var activity = tenantActivityDetails(body);
    if (activity && kind === STATEMENT_KIND_TENANT_ADMIN_ACTION) {
        // The action, its outcome and what it named are StatementExplanation's lines.
        view.origin = ELogOrigin.HARVEST;
        view.who = __assign(__assign({}, view.who), { username: (_e = view.who.username) !== null && _e !== void 0 ? _e : text(activity.username), userId: (_f = view.who.userId) !== null && _f !== void 0 ? _f : text(activity.user_id) });
    }
    if (isObject(body) && Array.isArray(body[STATEMENT_KIND_ADMIN_PUBLIC_KEY])) {
        view.publicKey = keyText(body[STATEMENT_KIND_ADMIN_PUBLIC_KEY][2]);
    }
    if (kind === "ConfigurationPackageImported" && isObject(body)) {
        var imported = (_g = body.ConfigurationPackageWithContentHash) !== null && _g !== void 0 ? _g : body.ConfigurationPackage;
        if (Array.isArray(imported) && isObject(imported[1]))
            view.extra = rows(imported[1]);
    }
    if (!details)
        return view;
    switch (kind) {
        case STATEMENT_KIND_VOTERS_IMPORTED:
        case STATEMENT_KIND_READINESS_VERIFIED: {
            var actor = isObject(details.actor) ? details.actor : {};
            view.who = __assign(__assign({}, view.who), { username: (_h = view.who.username) !== null && _h !== void 0 ? _h : text(actor.username), userId: (_j = view.who.userId) !== null && _j !== void 0 ? _j : text(actor.user_id), role: text(actor.role) });
            view.extra = rows(details, ["actor"]);
            break;
        }
        case STATEMENT_KIND_ADMIN_CHANGE: {
            var actor = isObject(details.actor) ? details.actor : {};
            var object = isObject(details.object) ? details.object : null;
            var extra = isObject(details.extra) ? details.extra : null;
            view.who = __assign(__assign({}, view.who), { username: (_k = view.who.username) !== null && _k !== void 0 ? _k : text(actor.username), userId: (_l = view.who.userId) !== null && _l !== void 0 ? _l : text(actor.user_id), role: text(actor.role) });
            view.origin = changeOrigin(details.origin);
            view.object =
                object && text(object.type)
                    ? { type: String(object.type), name: text(object.name), id: text(object.id) }
                    : null;
            view.action = text(details.action);
            view.changes = (Array.isArray(details.changes) ? details.changes : [])
                .map(changeOf)
                .filter(function (change) { return change !== null; });
            view.deletedEvent = deletedEventOf(extra);
            view.importedFile = importedFileOf(extra);
            view.extra = extra
                ? rows(extra, __spreadArray(__spreadArray([], (view.deletedEvent ? DELETED_EVENT_KEYS : []), true), (view.importedFile ? IMPORTED_FILE_KEYS : []), true))
                : [];
            break;
        }
        case STATEMENT_KIND_KEYCLOAK_ADMIN_EVENT: {
            view.keycloak = keycloakOf(details);
            view.origin = view.keycloak.origin;
            view.who = __assign(__assign({}, view.who), { username: (_m = view.who.username) !== null && _m !== void 0 ? _m : view.keycloak.username, userId: (_o = view.who.userId) !== null && _o !== void 0 ? _o : view.keycloak.userId });
            break;
        }
        case STATEMENT_KIND_LOGS_EXPORTED:
            view.exported = exportOf(details);
            break;
        case STATEMENT_KIND_LOG_WRITER_STARTED:
        case STATEMENT_KIND_LOG_WRITER_STOPPED:
            view.writer = {
                release: text(details.release),
                lastEntryId: text(details.last_entry_id),
            };
            break;
    }
    return view;
};
/** A key or signature short enough for a line: its start and end around an ellipsis. */
export var shortened = function (value, head, tail) {
    if (head === void 0) { head = 20; }
    if (tail === void 0) { tail = 5; }
    return value.length <= head + tail + 1 ? value : "".concat(value.slice(0, head), "\u2026").concat(value.slice(-tail));
};
